import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n';
import { generateReply } from '../services/ai';
import StyledMarkdown from './StyledMarkdown';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';

interface ParagraphReplyPanelProps {
  blockId: string;
  originalText: string;
  sectionTitle: string;
  sectionContent: string;
  projectName: string;
  locale?: string;
  existingReplies: { prompt: string; response: string; createdAt: string }[];
  onSave: (blockId: string, originalText: string, prompt: string, response: string) => void;
  onClose: () => void;
}

export default function ParagraphReplyPanel({
  blockId,
  originalText,
  sectionTitle,
  sectionContent,
  projectName,
  locale: localeProp,
  existingReplies,
  onSave,
  onClose,
}: ParagraphReplyPanelProps) {
  const { t, locale: appLocale } = useLanguage();
  const locale = (localeProp as 'en' | 'de') || appLocale;
  const d = t.document;
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const buildContext = () => {
    return locale === 'de'
      ? `Projekt: ${projectName}
Abschnitt: ${sectionTitle}

Originaler Abschnittsinhalt:
${sectionContent}

Ausgewählter Absatz / Block, auf den der Nutzer antwortet:
"""
${originalText}
"""`
      : `Project: ${projectName}
Section: ${sectionTitle}

Original section content:
${sectionContent}

Selected paragraph / block the user is replying to:
"""
${originalText}
"""`;
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    try {
      const result = await generateReply({ prompt: prompt.trim(), context: buildContext(), locale });
      setReply(result.content);
      onSave(blockId, originalText, prompt.trim(), result.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get reply');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white shadow-2xl border-l border-cloud z-50 flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-cloud">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-red-500" />
          <h3 className="font-semibold text-ink">{d.replyTitle}</h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate hover:bg-mist"
          aria-label="Close reply panel"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div className="bg-mist border border-cloud p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate mb-2">{d.selectedText}</p>
          <p className="text-sm text-ink line-clamp-6">{originalText}</p>
        </div>

        {existingReplies.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate">{d.previousReplies}</p>
            {existingReplies.map((r, i) => (
              <div key={i} className="bg-red-50 border border-red-100 p-4">
                <p className="text-xs text-slate mb-2">{t.document.reply}: {r.prompt}</p>
                <div className="prose max-w-none">
                  <StyledMarkdown content={r.response} />
                </div>
              </div>
            ))}
          </div>
        )}

        {reply && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate">{d.latestReply}</p>
            <div className="bg-white border border-cloud p-4 shadow-sm">
              <div className="prose max-w-none">
                <StyledMarkdown content={reply} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-100 text-sm text-red-700">{error}</div>
        )}
      </div>

      <div className="border-t border-cloud p-5 space-y-3">
        <textarea
          ref={inputRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={d.replyPlaceholder}
          rows={3}
          className="w-full px-3 py-2 border border-cloud bg-paper focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm resize-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-ash">{d.shortcut}</span>
          <button
            onClick={handleSubmit}
            disabled={loading || !prompt.trim()}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-sm font-medium flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {d.thinking}
              </>
            ) : (
              <>
                <Send size={14} />
                {d.send}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
